/* eslint-disable max-len */
/* eslint-env browser */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseUrl = '/api/';
export const backendApi = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  tagTypes: ['Studentorientation', 'SystemMessages', 'ExamNotifications', 'StudentCard'],
  endpoints: (builder) => ({
    getEnrolments: builder.query<IStudentEnrolment, number>({
      query: (payload: number) => `enrolments/${payload}`,
    }),
    getStudentorientation: builder.query<IStudentorientation[], IEnrolmentPayloadWithAuIdAndDate>({
      query: (payload: IEnrolmentPayloadWithAuIdAndDate) => `studentorientation/${payload.auId}?lang=${payload.lang}&date=${payload.date}&educations=${payload.educations.join(',')}&faculties=${payload.faculties.join(',')}&campuses=${payload.campuses.join(',')}`,
      providesTags: ['Studentorientation'],
    }),
    getStudentOrientationWithAdmissionCode: builder.query<IStudentOrientation[], IStudentOrientationPayload>({
      query: (payload: IStudentOrientationPayload) => `studentorientation/${payload.admissionAreaCode}/${payload.period}/?lang=${payload.lang}`,
    }),
    answer: builder.mutation<IAnswerResponse, IAnswerPayload>({
      query: (payload: IAnswerPayload) => ({
        url: `applications/answer/${payload.auId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      }),
    }),
    getSelfservices: builder.query<IShortcut[], IEnrolmentPayload>({
      query: (payload: IEnrolmentPayload) => `selfservice/?lang=${payload.lang}&educations=${payload.educations.join(',')}&faculties=${payload.faculties.join(',')}&campuses=${payload.campuses.join(',')}`,
    }),
    getStudyportals: builder.query<IStudyportal[], IEnrolmentPayload>({
      query: (payload: IEnrolmentPayload) => `studyportal/?lang=${payload.lang}&educations=${payload.educations.join(',')}&faculties=${payload.faculties.join(',')}&campuses=${payload.campuses.join(',')}`,
    }),
    getFeeds: builder.query<IFeed[], IEnrolmentPayload>({
      query: (payload: IEnrolmentPayload) => `feed/?lang=${payload.lang}&educations=${payload.educations.join(',')}&faculties=${payload.faculties.join(',')}&campuses=${payload.campuses.join(',')}`,
    }),
    getParsedFeed: builder.query<INewsEvent[], string>({
      query: (url: string) => `${baseUrl}feed/parse/?url=${url}`,
    }),
    getTimetable: builder.query<ITimetable, TimetablePayload>({
      query: (payload: TimetablePayload) => `timetable/${payload.auId}/${payload.date}`,
    }),
    getiCalKey: builder.query<string, iCalPayload>({
      query: (payload: iCalPayload) => `synchronize/${payload.auId}/${payload.regenerate}`,
    }),
    getTimeline: builder.query<ITimelineEducation, TimelinePayload>({
      query: (payload: TimelinePayload) => `studentoverview/timeline/${payload.auId}/${payload.relationId}/${payload.lang}`,
    }),
    getDiagramme: builder.query<IDiagramme, DiagrammePayload>({
      query: (payload: DiagrammePayload) => `diagramme/${payload.lang}/${payload.lineId}`,
    }),
    getCourses: builder.query<EnrolledCoursesResponse, EnrolledCoursesPayload>({
      query: (payload: EnrolledCoursesPayload) => `course/${payload.auId}?lang=${payload.lang}`,
    }),
    getTranscriptCount: builder.query<number, ITranscriptCountPayload>({
      query: (payload: ITranscriptCountPayload) => `transcriptofrecords/${payload.auId}/${payload.lang}/count`,
    }),
    getMails: builder.query<IMail[], IM365Payload>({
      query: (payload: IM365Payload) => `mail/M365/${payload.auId}${payload.impersonating ? '?impersonating=true' : ''}`,
    }),
    getLMSAnnouncements: builder.query<ILMSAnnouncement[], number>({
      query: (payload: number) => `lms/${payload}`,
    }),
    dismissStudentorientation: builder.mutation<void, IPatchEntityPayload>({
      query: (payload: IPatchEntityPayload) => ({
        url: `studentorientation/${payload.auId}/dismiss/${payload.id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['Studentorientation'],
    }),
    getSystemMessages: builder.query<ISystemMessage[], ISystemMessagePayload>({
      query: (payload: ISystemMessagePayload) => {
        if (payload.auId > 0) {
          return `systemmessage/${payload.auId}?systemId=${payload.systemId}&lang=${payload.lang}&educations=${payload.educations.join(',')}&faculties=${payload.faculties.join(',')}&campuses=${payload.campuses.join(',')}`;
        }
        return `systemmessage/?lang=${payload.lang}&systemId=${payload.systemId}`;
      },
      providesTags: ['SystemMessages'],
    }),
    dismissMessage: builder.mutation<void, IDismissMessagePayload>({
      query: (payload: IDismissMessagePayload) => ({
        url: `systemmessage/${payload.auId}/dismiss/${payload.id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['SystemMessages'],
    }),
    getExamResults: builder.query<IExamNotification[], IExamNotificationsPayload>({
      query: (payload: IExamNotificationsPayload) => `examresult/${payload.auId}/${payload.lang}?relations=${payload.relations.join(',')}`,
      providesTags: ['ExamNotifications'],
    }),
    readExamNotification: builder.mutation<void, IPatchEntityPayload>({
      query: (payload: IPatchEntityPayload) => ({
        url: `examresult/${payload.auId}/${payload.id}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['ExamNotifications'],
    }),
    getCardOrder: builder.query<ICardOrder, number>({
      query: (payload: number) => `cardorder/${payload}`,
      providesTags: ['StudentCard'],
    }),
    submitCardOrder: builder.mutation<void, ICardOrderPayload>({
      query: (payload: ICardOrderPayload) => {
        const data = new FormData();
        data.append('file', payload.file);
        return {
          url: `cardorder/${payload.auId}?persist=${payload.persist}`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: ['StudentCard'],
    }),
    getIsSuCandidate: builder.query<boolean, number>({
      query: (payload: number) => `enrolments/${payload}/is-su-candidate`,
    }),
    getMapData: builder.query<IMapLocation[], void>({
      query: () => 'https://aufind.au.dk/web/xmltojson.php?lang=1&getdepartments=false&getoverlays=false',
      transformResponse: (response: IMapResponse) => response.locations,
    }),
    searchPerson: builder.query<IEmployee[], string>({
      query: (payload: string) => `https://aufind.au.dk/searchPeople.php?q=${payload}&mode=json`,
    }),
    getCurrentSemester: builder.query<ICurrentSemester, string>({
      query: (payload: string) => `studentoverview/current-semester/${payload}`,
    }),
    getMigrationStatus: builder.query<IMigrationStatus, number>({
      query: (payload) => `mail/migration/${payload}`,
    }),
    getApplications: builder.query<IBachelorApplication[], IBachelorApplicationPayload>({
      query: (payload: IBachelorApplicationPayload) => `applications/${payload.auId}?lang=${payload.lang}`,
    }),
  }),
});

export const {
  useGetEnrolmentsQuery,
  useGetStudentorientationQuery,
  useGetSelfservicesQuery,
  useGetTimetableQuery,
  useGetiCalKeyQuery,
  useGetStudyportalsQuery,
  useGetFeedsQuery,
  useGetTimelineQuery,
  useGetDiagrammeQuery,
  useGetCoursesQuery,
  useGetTranscriptCountQuery,
  useGetParsedFeedQuery,
  useGetMailsQuery,
  useGetLMSAnnouncementsQuery,
  useDismissStudentorientationMutation,
  useGetSystemMessagesQuery,
  useDismissMessageMutation,
  useGetExamResultsQuery,
  useReadExamNotificationMutation,
  useGetCardOrderQuery,
  useSubmitCardOrderMutation,
  useGetIsSuCandidateQuery,
  useGetMapDataQuery,
  useLazySearchPersonQuery,
  useGetCurrentSemesterQuery,
  useGetMigrationStatusQuery,
  useGetStudentOrientationWithAdmissionCodeQuery,
  useGetApplicationsQuery,
} = backendApi;
